<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Notas de pedido</h1>
          <p class="view-header__subtitle">Gestioná las notas de pedido y su estado.</p>
        </div>
        <div class="view-header__actions">
          <router-link :to="nuevaNotaDePedidoLink" class="btn view-header__btn view-header__btn--primary">
            <i class="bi bi-plus-lg"></i>
            Nueva nota de pedido
          </router-link>
        </div>
      </div>
    </div>
    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar...">
          </div>
          <div class="filters-panel__field">
            <div class="form-check">
              <input id="oninvoiced" v-model="oninvoiced" class="form-check-input" type="checkbox">
              <label class="form-check-label" for="oninvoiced">Mostrar notas ya facturadas</label>
            </div>
          </div>
          <div class="filters-panel__field filters-panel__field--inline">
            <label class="control-label">Fecha:</label>
            <Daterange ref="daterange" :opendir="'left'" :initial-start="dateRangeStart" :initial-end="dateRangeEnd" @datepicked="onDatePicked" />
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="clearFilters">
              <i class="bi bi-eraser"></i>
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
        @orderNoteDetail="getOrderNoteDetail"
        @convertOrderNoteToSale="convertOrderNoteToSaleMethod"
        @getOrderNotePDF="downloadOrderNotePDF"
        @delete="deleteOrderNote"
        @setPendiente="(data) => updateOrderNoteStatus(data, 'PENDING')"
        @setPreparando="(data) => updateOrderNoteStatus(data, 'MAKING')"
        @setTerminada="(data) => updateOrderNoteStatus(data, 'READY')"
        @setAnulada="(data) => updateOrderNoteStatus(data, 'CANCELED')"
      />
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import { mapState } from 'vuex';
import Daterange from "../../global/Daterange-component.vue";
import formatDateMixin from "../../../mixins/formatDateMixin";
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";
import myaxios from 'axios';

const STATUS_LABELS = {
  PENDING: 'Pendiente',
  MAKING: 'Preparando',
  READY: 'Terminada',
  CANCELED: 'Anulada',
};

const STATUS_VARIANTS = {
  PENDING: 'warning',
  MAKING: 'accent',
  READY: 'success',
  CANCELED: 'danger',
};

export default {
  components: {
    VdtnetTable,
    Daterange,
  },
  mixins: [formatDateMixin, formatMoneyMixin],
  data: function () {
    const defaultRange = this.last30DaysRange();
    return {
      doSearch: '',
      oninvoiced: false,
      isLoading: false,
      dateRangeStart: defaultRange.startDate,
      dateRangeEnd: defaultRange.endDate,
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/ordernotes/list",
          type: "GET",
          data: (d) => {
            d.oninvoiced = this.oninvoiced;
            d.branchoffice_id = this.branchofficeId;
            d.daterange = this.daterange;
            return d;
          },
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        processing: false,
        pageLength: 15,
        searching: true,
        searchDelay: 1500,
        destroy: true,
        ordering: true,
        lengthChange: true,
        serverSide: true,
        fixedHeader: true,
        autoWidth: false,
      },
      fields: {
        id: { visible: false },
        number: { label: "N°", width: "70px" },
        created_at: {
          label: "Fecha",
          width: "120px",
          render: (data) => new Date(data).toLocaleDateString('es-AR'),
        },
        client: {
          label: "Cliente",
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        user_id: { label: "Vendedor", width: "120px" },
        branchoffice: { label: "Sucursal", width: "120px" },
        total: {
          label: "Total",
          width: "110px",
          align: "right",
          render: (data, type) => {
            const formatted = this.formatoDinamicoMoneda(data, type);
            return type === 'display' ? `<span class="data-table__primary">${formatted}</span>` : formatted;
          },
        },
        status: {
          label: "Estado",
          width: "110px",
          render: (data, type) => {
            if (type !== 'display') return data;
            const label = STATUS_LABELS[data] || 'Desconocido';
            const variant = STATUS_VARIANTS[data] || 'neutral';
            return `<span class="status-badge status-badge--${variant}">${label}</span>`;
          },
        },
        stocked: { label: "Stock", width: "80px", align: "center", searchable: false },
        actions: {
          isLocal: true,
          label: "Acciones",
          width: "200px",
          align: "right",
          defaultContent: `
            <div class="table-action-group">
              <button data-action="orderNoteDetail" class="table-action-btn" type="button" title="Ver detalle">
                <i class="bi bi-file-earmark-text"></i>
              </button>
              <button data-action="getOrderNotePDF" class="table-action-btn" type="button" title="Descargar PDF">
                <i class="bi bi-filetype-pdf"></i>
              </button>
              <button data-action="convertOrderNoteToSale" class="table-action-btn" type="button" title="Convertir en venta">
                <i class="bi bi-basket"></i>
              </button>
              <div class="dropdown">
                <button class="table-action-btn dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" title="Cambiar estado">
                  <i class="bi bi-arrow-repeat"></i>
                </button>
                <div class="dropdown-menu dropdown-menu-right">
                  <a class="dropdown-item" data-action="setPendiente" href="javascript:void(0)">Pendiente</a>
                  <a class="dropdown-item" data-action="setPreparando" href="javascript:void(0)">Preparando</a>
                  <a class="dropdown-item" data-action="setTerminada" href="javascript:void(0)">Terminada</a>
                  <a class="dropdown-item" data-action="setAnulada" href="javascript:void(0)">Anulada</a>
                </div>
              </div>
              <button data-action="delete" class="table-action-btn" type="button" title="Eliminar comprobante">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          `,
        },
      },
    };
  },
  computed: {
    daterange() {
      if (!this.dateRangeStart || !this.dateRangeEnd) return null;
      return `${this.formatoFecha2Daterange(this.dateRangeStart)} - ${this.formatoFecha2Daterange(this.dateRangeEnd)}`;
    },
    nuevaNotaDePedidoLink() {
      localStorage.removeItem('header');
      localStorage.removeItem('products');
      return { name: 'Nueva nota de pedido' };
    },
    ...mapState('branchoffices', {
      branchofficeId: state => state.selectedBranchofficeId,
    }),
  },
  mounted() {
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  watch: {
    oninvoiced() {
      this.refreshTable();
    },
    doSearch(newValue, oldValue) {
      if (newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch);
      }
      if (newValue === '' && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch);
      }
    },
    branchofficeId() {
      this.refreshTable();
    },
    daterange() {
      this.refreshTable();
    },
  },
  methods: {
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    onDatePicked(date) {
      this.dateRangeStart = date.startDate;
      this.dateRangeEnd = date.endDate;
    },
    clearFilters() {
      this.doSearch = '';
      this.oninvoiced = false;
      const defaultRange = this.last30DaysRange();
      this.dateRangeStart = defaultRange.startDate;
      this.dateRangeEnd = defaultRange.endDate;
    },
    getOrderNoteDetail(data) {
      this.$router.push({ name: 'Detalle de nota de pedido', params: { orderNoteId: data.id } });
    },
    convertOrderNoteToSaleMethod(data) {
      this.$router.push({ name: 'Nueva venta desde ON', params: { orderNoteId: data.id } });
    },
    downloadOrderNotePDF(data) {
      const filename = data.client + data.updated_at + '.pdf';
      this.$http.get("/api/ordernotes/onPDF/" + data.id).then((res) => {
        const uninterceptedAxiosInstance = myaxios.create();
        uninterceptedAxiosInstance.get(res.data, { responseType: 'blob' }).then((blobRes) => {
          const blob = new Blob([blobRes.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
        });
      });
    },
    updateOrderNoteStatus(data, newStatus) {
      if (data.status === "CANCELED") {
        return alert('El estado actual no puede modificarse');
      }
      const message = newStatus === "CANCELED"
        ? "Esta acción no puede deshacerse: ¿Está seguro que desea anular esta nota de pedido?"
        : "¿Está seguro que desea modificar el estado de esta nota de pedido?";
      if (!confirm(message)) return;
      this.$http.post("/api/ordernotes/update", { id: data.id, status: newStatus }).then((response) => {
        const code = response.data.code;
        if (code === 200 || code === 201) this.refreshTable();
      });
    },
    deleteOrderNote(data) {
      if (!confirm("¿Está seguro que desea borrar esta nota de pedido?")) return;
      this.$http.post("/api/ordernotes/delete", { id: data.id }).then((response) => {
        if (response.data.code === 201) this.refreshTable();
      });
    },
  },
};
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>
